import type { LotSummary, Suggestions } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { deleteLot, updateLot } from "../api.js";
import { useMayWrite, useSeesPrices } from "../cellar.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";
import { Picker } from "./Picker.js";

/**
 * What was bought, and the one way back out of a purchase entered wrongly.
 *
 * A lot is one acquisition of one wine at one price on one date. It can be
 * deleted outright for exactly as long as nothing has happened to its bottles,
 * which is project-scope.md's rule and is why the button appears on some rows
 * and not others: until something has happened the lot is a typing mistake and
 * deleting it loses nothing, and after that it is a real acquisition whose
 * history is the most valuable thing here.
 *
 * The server decides which case a lot is in and says so on the row. The client
 * could not work it out: it sees that a bottle is standing in the cellar, not
 * that it was moved there from another shelf yesterday.
 *
 * Deleting asks first, and it is the only thing in the application that does.
 * Everything else that removes a bottle from view writes an event that can be
 * undone from the confirmation; this one takes the bottles with it, so the
 * question comes before rather than the way back after.
 *
 * **Correcting is the other act, and it keeps none of those conditions.**
 * `project-scope.md`, 2026-09-11: the price, the date, the currency and the
 * provenance describe one acquisition and assert that nothing happened, so a
 * wrong one can be put right at any time — including years later, with the
 * bottles long drunk. The quantity is deliberately not on that form: bottles
 * are created and taken away through their own operations, which leave events
 * behind, and a number edited here would make physical objects appear and
 * disappear in silence.
 */
export function LotList({
  cellarId,
  wineId,
  lots,
  systembolagetId,
  suggestions,
  onDeleted,
}: {
  cellarId: string;
  wineId: string;
  lots: readonly LotSummary[];
  /** The wine's article number, which the correction form shows and may change. */
  systembolagetId: string | null;
  /** What this cellar has bought through before, for the correction form. */
  suggestions: Suggestions | null;
  /** Refetch: the lot and its bottles are gone from the wine. */
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const mayWrite = useMayWrite();
  const seesPrices = useSeesPrices();

  const remove = (lot: LotSummary) => {
    setBusy(true);
    setError(null);
    void deleteLot(cellarId, wineId, lot.id)
      .then(() => {
        setAsking(null);
        setNotice(t("wine.lotDeleted"));
        onDeleted();
      })
      .catch(() => setError(t("wine.lotDeleteFailed")))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <ul className="border-line mt-3">
        {lots.map((lot) => (
          <li key={lot.id} className="border-line border-b py-3 text-sm">
            {/* Two different silences, and the row must not confuse them. A
                dash is *nothing was recorded*, which the cellar has a great
                deal of — 37 lots of 125 carry no price. *Visas inte* is this
                cellar's answer to a guest, and it is the cellar saying so
                rather than the record being empty. The first comes from the
                field, the second from `seesPrices`; where the reader may not
                see them, every one of these fields arrived as null and the
                row has only the count to show. */}
            <div className="flex items-baseline justify-between gap-4">
              <span>
                {seesPrices === false
                  ? t("wine.lotBottles", { count: lot.quantity })
                  : `${lot.quantity} × ${lot.source ?? ""}${
                      lot.purchasedOn === null ? "" : ` · ${lot.purchasedOn}`
                    }`}
              </span>
              {seesPrices === false ? (
                <span className="text-muted">{t("wine.lotNotShown")}</span>
              ) : (
                <span className="font-display">
                  {lot.pricePerBottle === null
                    ? "—"
                    : `${lot.pricePerBottle} ${lot.currency ?? ""}`}
                </span>
              )}
            </div>

            {/* The two provenance fields, shown when a bottle is opened
                because "given by Erik, Linnea and Johan for my sixtieth" is
                precisely what one wants to be told at that moment. */}
            {(lot.receivedFrom !== null || lot.promptedBy !== null) && (
              <p className="text-muted mt-1">
                {[lot.receivedFrom, lot.promptedBy].filter(Boolean).join(" · ")}
              </p>
            )}

            {mayWrite === true && asking !== lot.id && editing !== lot.id && (
              <div className="mt-2 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(lot.id);
                    setError(null);
                    setNotice(null);
                  }}
                  className="text-accent underline-offset-4 hover:underline"
                >
                  {t("wine.editLot")}
                </button>
                {lot.untouched && (
                  <button
                    type="button"
                    onClick={() => {
                      setAsking(lot.id);
                      setError(null);
                      setNotice(null);
                    }}
                    className="text-muted underline-offset-4 hover:underline"
                  >
                    {t("wine.deleteLot")}
                  </button>
                )}
              </div>
            )}

            {editing === lot.id && (
              <LotForm
                cellarId={cellarId}
                wineId={wineId}
                lot={lot}
                systembolagetId={systembolagetId}
                suggestions={suggestions}
                onDone={() => {
                  setEditing(null);
                  setNotice(t("wine.lotSaved"));
                  onDeleted();
                }}
                onCancel={() => setEditing(null)}
              />
            )}

            {asking === lot.id && (
              <div className="mt-2">
                {/* Says what goes with it, in bottles rather than in rows: the
                    number that matters is the one standing in the cabinet. */}
                <p className="max-w-prose">
                  {t("wine.deleteLotAsk", { count: lot.quantity })}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => remove(lot)}
                    className="text-accent underline-offset-4 hover:underline disabled:opacity-40"
                  >
                    {busy ? t("wine.saving") : t("wine.deleteLot")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAsking(null)}
                    className="text-muted underline-offset-4 hover:underline"
                  >
                    {t("wines.cancel")}
                  </button>
                  {error !== null && (
                    <span role="alert" className="text-past">
                      {error}
                    </span>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {notice !== null && (
        <p role="status" className="text-muted mt-3 text-sm">
          {notice}
        </p>
      )}
    </>
  );
}

/**
 * The lot's own facts, and only those.
 *
 * Six fields, no quantity and no shelf. What a purchase cost and when it
 * happened is a description; how many bottles there are is a count of objects
 * standing in a cabinet, and the two must not be editable in the same gesture.
 *
 * A seventh that is not the lot's: the wine's article number, here because
 * this form is what gets opened with the receipt in hand, for a lot registered
 * before the field existed.
 */
function LotForm({
  cellarId,
  wineId,
  lot,
  systembolagetId: winesNumber,
  suggestions,
  onDone,
  onCancel,
}: {
  cellarId: string;
  wineId: string;
  lot: LotSummary;
  systembolagetId: string | null;
  suggestions: Suggestions | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [purchasedOn, setPurchasedOn] = useState(lot.purchasedOn ?? "");
  const [price, setPrice] = useState(
    lot.pricePerBottle === null ? "" : String(lot.pricePerBottle),
  );
  const [currency, setCurrency] = useState(lot.currency ?? "SEK");
  // Null only for a reader who may not see the lot's data, and such a reader
  // never reaches this form — the row above it draws no buttons for them.
  const [source, setSource] = useState(lot.source ?? "");
  const [receivedFrom, setReceivedFrom] = useState(lot.receivedFrom ?? "");
  const [promptedBy, setPromptedBy] = useState(lot.promptedBy ?? "");
  // Null until touched, and sent only then: the number is the wine's, and a
  // price corrected on one lot must not write back a number that was changed
  // on another lot's form, or in another tab, since this one was opened.
  const [systembolagetId, setSystembolagetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const priceGiven = price.trim() !== "";
  const canSubmit = !busy && (!priceGiven || Number.isFinite(Number(price)));

  return (
    <form
      className="border-line mt-3 grid gap-4 border-t pt-4 sm:grid-cols-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        void updateLot(cellarId, wineId, lot.id, {
          purchasedOn: purchasedOn === "" ? null : purchasedOn,
          pricePerBottle: priceGiven ? Number(price) : null,
          // A price and its currency stand or fall together, which is why an
          // emptied price takes its currency with it rather than leaving a
          // three-letter code attached to nothing.
          currency: priceGiven ? currency : null,
          source,
          note: null,
          receivedFrom: receivedFrom.trim() === "" ? null : receivedFrom,
          promptedBy: promptedBy.trim() === "" ? null : promptedBy,
          ...(systembolagetId !== null && {
            systembolagetId: systembolagetId.trim() === "" ? null : systembolagetId,
          }),
        })
          .then(onDone)
          .catch(() => setError(t("wine.lotSaveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`lot-date-${lot.id}`}>
          {t("wine.purchasedOn")}
        </label>
        <input
          id={`lot-date-${lot.id}`}
          type="date"
          value={purchasedOn}
          onChange={(event) => setPurchasedOn(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`lot-price-${lot.id}`}>
          {t("wine.price")}
        </label>
        <input
          id={`lot-price-${lot.id}`}
          inputMode="decimal"
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`lot-currency-${lot.id}`}>
          {t("wine.currency")}
        </label>
        <input
          id={`lot-currency-${lot.id}`}
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      {/* A picker rather than the dropdown of five it was until `9.11`: the
          cellar's own sources are suggested and anything else can be typed,
          which is what a bottle bought through Vivino needed. */}
      <Picker
        id={`lot-source-${lot.id}`}
        label={t("wine.source")}
        value={source}
        onChange={setSource}
        options={(suggestions?.purchaseSources ?? []).map((row) => row.name)}
        placeholder={t("wine.sourceHint")}
      />

      {/* Beside the source, as on the registering form, because both are read
          off the same receipt. */}
      <div>
        <label className={fieldLabel} htmlFor={`lot-sb-${lot.id}`}>
          {t("wine.systembolagetId")}
        </label>
        <input
          id={`lot-sb-${lot.id}`}
          value={systembolagetId ?? winesNumber ?? ""}
          onChange={(event) => setSystembolagetId(event.target.value)}
          placeholder={t("wine.systembolagetIdHint")}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`lot-from-${lot.id}`}>
          {t("wine.receivedFrom")}
        </label>
        <input
          id={`lot-from-${lot.id}`}
          value={receivedFrom}
          onChange={(event) => setReceivedFrom(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`lot-prompt-${lot.id}`}>
          {t("wine.promptedBy")}
        </label>
        <input
          id={`lot-prompt-${lot.id}`}
          value={promptedBy}
          onChange={(event) => setPromptedBy(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div className="col-span-full flex items-center gap-4">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.saveLot")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
        >
          {t("wine.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
